import { computed, inject, Injectable, signal } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { BackendService } from '@ipg/ngx-frontend-toolset';

export interface VarResponse {
  id: string;
  key: string;
  value: string;
  description?: string;
  deleteAt?: string;
}

export interface ClientVarResponse {
  baseVarId: string;
  key: string;
  baseValue: string;
  value: string;
  description?: string;
  isOverridden: boolean;
}

export interface ClientVarOverrideItem {
  baseVarId: string;
  value: string;
}

@Injectable({
  providedIn: 'root',
})
export class VarService {
  private readonly _backend = inject(BackendService);
  private readonly _vars = signal<VarResponse[]>([]);
  private readonly _pendingDeletionVars = signal<VarResponse[]>([]);
  private readonly _clientVars = signal<ClientVarResponse[]>([]);
  private readonly _builtinVars = signal<Map<string, string>>(new Map());

  readonly knownKeys = computed(() => new Set(this._vars().map((v) => v.key)));
  readonly varMap = computed(() => {
    const map = new Map<
      string,
      { value: string; description?: string; deleteAt?: string }
    >();
    // Pending-deletion vars first (lowest priority)
    for (const v of this._pendingDeletionVars()) {
      if (v.deleteAt) {
        map.set(v.key, { value: v.value, deleteAt: v.deleteAt });
      }
    }
    // Active vars override pending-deletion
    for (const v of this._vars()) {
      map.set(v.key, { value: v.value, description: v.description });
    }
    // Client-specific values override base values
    for (const cv of this._clientVars()) {
      map.set(cv.key, { value: cv.value, description: cv.description });
    }
    // Builtin vars (highest priority, e.g. roepnaam)
    for (const [key, value] of this._builtinVars()) {
      map.set(key, { value });
    }
    return map;
  });
  readonly vars = this._vars.asReadonly();
  readonly pendingDeletionVars = this._pendingDeletionVars.asReadonly();

  getAll(): Observable<VarResponse[]> {
    return this._backend
      .get<VarResponse[]>('/vars')
      .pipe(tap((vars) => this._vars.set(vars)));
  }

  getPendingDeletionVars(): Observable<VarResponse[]> {
    return this._backend
      .get<VarResponse[]>('/vars/pending-deletion')
      .pipe(tap((vars) => this._pendingDeletionVars.set(vars)));
  }

  createVar(data: {
    key: string;
    value: string;
    description?: string;
  }): Observable<VarResponse> {
    return this._backend
      .post<VarResponse>('/vars', data)
      .pipe(tap((created) => this._vars.update((vars) => [...vars, created])));
  }

  updateVar(
    varId: string,
    data: { value: string; description?: string }
  ): Observable<VarResponse> {
    return this._backend
      .put<VarResponse>(`/vars/${varId}`, data)
      .pipe(
        tap((updated) =>
          this._vars.update((vars) => vars.map((v) => (v.id === varId ? updated : v)))
        )
      );
  }

  deleteVar(varId: string): Observable<VarResponse> {
    return this._backend.delete<VarResponse>(`/vars/${varId}`).pipe(
      tap((deleted) => {
        this._vars.update((vars) => vars.filter((v) => v.id !== varId));
        this._pendingDeletionVars.update((vars) => [...vars, deleted]);
      })
    );
  }

  restoreVar(varId: string): Observable<VarResponse> {
    return this._backend.post<VarResponse>(`/vars/${varId}/restore`, {}).pipe(
      tap((restored) => {
        this._pendingDeletionVars.update((vars) => vars.filter((v) => v.id !== varId));
        this._vars.update((vars) => [...vars, restored]);
      })
    );
  }

  getVarRefCount(varId: string): Observable<{ overrides: number; usages: number }> {
    return this._backend.get<{ overrides: number; usages: number }>(
      `/vars/${varId}/refs/count`
    );
  }

  getClientVars(clientId: string): Observable<ClientVarResponse[]> {
    return this._backend
      .get<ClientVarResponse[]>(`/clients/${clientId}/vars`)
      .pipe(tap((vars) => this._clientVars.set(vars)));
  }

  clearClientVars(): void {
    this._clientVars.set([]);
  }

  setBuiltinVar(key: string, value: string): void {
    this._builtinVars.update((m) => {
      const next = new Map(m);
      next.set(key, value);
      return next;
    });
  }

  clearBuiltinVars(): void {
    this._builtinVars.set(new Map());
  }

  updateClientVars(
    clientId: string,
    overrides: ClientVarOverrideItem[]
  ): Observable<ClientVarResponse[]> {
    return this._backend.put<ClientVarResponse[]>(`/clients/${clientId}/vars`, {
      overrides,
    });
  }
}
